import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import LinkIcon from '@mui/icons-material/Link';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TabletIcon from '@mui/icons-material/Tablet';
import TuneIcon from '@mui/icons-material/Tune';
import TvIcon from '@mui/icons-material/Tv';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Stack,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import { useGlobalData } from '@/contexts/GlobalDataProvider';
import { MessageType, WebSocketActionType } from '@/enums';
import StreamingEngineService, {
  type StreamingEngineSession,
  type StreamingEngineSessionState,
  type UserSession,
} from '@/services/streaming-engine-service';
import { LocalStorageService } from '@/services/local-storage';
import { applicationDataSelector } from '@/store/slices/application-data';

import { camelCase } from 'lodash';

import useAppUtils from '../../utils/appUtils';
import clientUtils from '../../utils/clientUtils';
import { dateUtils } from '../../utils/dateUtils';

// ─── Types ───────────────────────────────────────────────────────────────────

type MonitoringView = 'list' | 'sessionDetail' | 'userSessionDetail';

/** Theme tokens: SCSS maps `$variables` → these names on `.channel-monitoring-modal` (see channel-monitoring-modal.scss). */
const mon = {
  iconMuted: 'var(--channel-monitoring-icon-muted)',
  iconFaint: 'var(--channel-monitoring-icon-faint)',
  borderPaper: 'var(--channel-monitoring-border-paper)',
  textSection: 'var(--channel-monitoring-text-section)',
  textMuted: 'var(--channel-monitoring-text-muted)',
  textPrimary: 'var(--channel-monitoring-text-primary)',
  divider: 'var(--channel-monitoring-divider)',
} as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string, timezone: string) => {
  if (!dateStr || dateStr.startsWith('0001-01-01')) return '—';
  try {
    return dateUtils.format(dateStr, timezone, 'M/d/yyyy h:mm:ss a') ?? dateStr;
  } catch {
    return dateStr;
  }
};

const formatDateInline = (dateStr: string, timezone: string) => {
  if (!dateStr || dateStr.startsWith('0001-01-01')) return '—';
  try {
    return dateUtils.format(dateStr, timezone, 'MMM d, yyyy h:mm a') ?? dateStr;
  } catch {
    return dateStr;
  }
};

const formatDuration = (startStr: string, stopStr?: string, opts?: { fallBackToNow?: boolean }) => {
  if (!startStr || startStr.startsWith('0001-01-01')) return '—';
  try {
    const start = DateTime.fromISO(startStr);
    const fallBackToNow = opts?.fallBackToNow !== false;
    const stop =
      stopStr && !stopStr.startsWith('0001-01-01')
        ? DateTime.fromISO(stopStr)
        : fallBackToNow
          ? DateTime.utc()
          : null;
    if (!stop) return '—';
    const diff = stop.diff(start, ['hours', 'minutes', 'seconds']);
    const h = Math.floor(diff.hours);
    const m = Math.floor(diff.minutes);
    const s = Math.floor(diff.seconds);
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 || h > 0) parts.push(`${m}m`);
    parts.push(`${s}s`);
    return parts.join(' ');
  } catch {
    return '—';
  }
};

const playbackCountFormatter = new Intl.NumberFormat();

const formatPlaybackCount = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return playbackCountFormatter.format(Math.round(Number(n)));
};

const formatPlaybackSeconds = (sec: number | null | undefined): string => {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  let total = Math.round(Number(sec));
  if (total < 0) total = 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || h > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
};

const safeNum = (v: unknown): number | undefined => {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

type PlaybackBreakdownTileProps = {
  title: string;
  count?: number | null;
  durationSeconds?: number | null;
  icon: React.ReactNode;
  /** Accent variant key — styled via `.cm-playback-tile--<variant>` in channel-monitoring-modal.scss. */
  variant: string;
};

const PlaybackBreakdownTile = ({ title, count, durationSeconds, icon, variant }: PlaybackBreakdownTileProps) => {
  const countStr = formatPlaybackCount(count);
  const durStr = formatPlaybackSeconds(durationSeconds);
  const hasCount = count != null && Number.isFinite(Number(count));
  const hasDur = durationSeconds != null && Number.isFinite(Number(durationSeconds));
  const durationFooter = hasDur ? `${durStr} total` : '0s total';

  return (
    <Box
      className={`cm-playback-tile cm-playback-tile--${variant}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        minHeight: 0,
        height: '100%',
        borderRadius: 2,
        p: '12px 14px',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
        <Box
          className="cm-playback-tile__icon"
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          className="cm-playback-tile__title"
          sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.3 }}
        >
          {title}
        </Typography>
      </Stack>
      <Typography variant="h4" fontWeight={600} sx={{ color: mon.textSection, mt: 0.5, lineHeight: 1, letterSpacing: '-0.02em' }}>
        {hasCount ? countStr : '—'}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: mon.textMuted,
          mt: 'auto',
          pt: 1,
          fontSize: '0.75rem',
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 14 }} />
        {durationFooter}
      </Typography>
    </Box>
  );
};

type PlaybackTotalSummaryProps = {
  count?: number | null;
  durationSeconds?: number | null;
};

const PlaybackTotalSummary = ({ count, durationSeconds }: PlaybackTotalSummaryProps) => {
  const countStr = formatPlaybackCount(count);
  const durStr = formatPlaybackSeconds(durationSeconds);
  const hasDur = durationSeconds != null && Number.isFinite(Number(durationSeconds));
  const durationFooter = hasDur ? `${durStr} total` : '0s total';

  return (
    <Box
      sx={{
        flex: { xs: '0 0 auto', md: '0 0 168px' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 0.75,
        py: 1,
        px: 1.5,
      }}
    >
      <Box
        className="cm-playback-total__icon"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BarChartOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Typography variant="body2" fontWeight={600} sx={{ color: mon.textSection, lineHeight: 1.3 }}>
        Total Assets
      </Typography>
      <Typography
        sx={{
          fontSize: '3rem',
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: '-0.03em',
          color: mon.textSection,
          my: 0.5,
        }}
      >
        {countStr}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: mon.textMuted,
          fontSize: '0.75rem',
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 14 }} />
        {durationFooter}
      </Typography>
    </Box>
  );
};

const asRecord = (val: unknown): Record<string, unknown> => val as Record<string, unknown>;

const isSessionRunning = (session: StreamingEngineSession | StreamingEngineSessionState): boolean => {
  const stopped =
    (session as StreamingEngineSessionState).sessionStoppedDateUtc ??
    asRecord(session).SessionStoppedDateUtc ??
    '';
  return !stopped || String(stopped).startsWith('0001-01-01');
};

const getSessionId = (session: StreamingEngineSession): string =>
  String(session.sessionId ?? asRecord(session).SessionId ?? '');

const getStartedDate = (session: StreamingEngineSession | StreamingEngineSessionState): string =>
  String(
    (session as StreamingEngineSessionState).sessionStartedDateUtc ??
    asRecord(session).SessionStartedDateUtc ??
    ''
  );

const getStoppedDate = (session: StreamingEngineSession | StreamingEngineSessionState): string =>
  String(
    (session as StreamingEngineSessionState).sessionStoppedDateUtc ??
    asRecord(session).SessionStoppedDateUtc ??
    ''
  );

const get = (obj: Record<string, unknown>, key: string): unknown =>
  obj[key] ?? obj[camelCase(key)] ?? obj[key.charAt(0).toUpperCase() + key.slice(1)];

/** Server echoes keep-alive traffic; omit from live tracing UI. */
const isKeepAliveWebSocketPayload = (msg: Record<string, unknown>): boolean => {
  const mt = get(msg, 'messageType');
  if (mt == null) return false;
  const s = String(mt);
  return s === MessageType.KeepAlive || s.toLowerCase() === 'keepalive';
};

const extractTimestamp = (msg: Record<string, unknown>): string | null => {
  const candidates = [
    get(msg, '_receivedAt'),
    get(msg, 'timestamp'),
    get(msg, 'Timestamp'),
    get(msg, 'timeStampUtc'),
    get(msg, 'TimeStampUtc'),
    get(msg, 'eventDateUtc'),
    get(msg, 'EventDateUtc'),
  ].filter(Boolean);
  for (const c of candidates) {
    const str = String(c);
    if (!str) continue;
    try {
      const formatted = DateTime.fromISO(str).toFormat('h:mm:ss a');
      if (formatted) return formatted;
    } catch {
      // continue
    }
  }
  return null;
};

const isEnrichedBool = (v: boolean | null | undefined): v is boolean => v === true || v === false;

/** API may return PascalCase or camelCase on enriched session objects. */
const enrichedProp = (
  ep: UserSession['EnrichedSessionProperties'] | null | undefined,
  pascalKey: string
): unknown => {
  if (!ep) return undefined;
  const r = ep as unknown as Record<string, unknown>;
  const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
  if (Object.prototype.hasOwnProperty.call(r, pascalKey)) return r[pascalKey];
  if (Object.prototype.hasOwnProperty.call(r, camelKey)) return r[camelKey];
  return undefined;
};

const enrichedBool = (
  ep: UserSession['EnrichedSessionProperties'] | null | undefined,
  pascalKey: string
): boolean | null | undefined => {
  const raw = enrichedProp(ep, pascalKey);
  if (raw === true || raw === false) return raw;
  if (raw === null) return null;
  if (typeof raw === 'string') {
    const s = raw.toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
  }
  return undefined;
};

const enrichedStr = (ep: UserSession['EnrichedSessionProperties'] | null | undefined, pascalKey: string): string => {
  const v = enrichedProp(ep, pascalKey);
  if (v == null) return '';
  return String(v);
};

const getDeviceInfo = (
  us: UserSession
): { label: string; os: string; color: 'info' | 'success' | 'secondary' | 'warning' } => {
  const ep = us.EnrichedSessionProperties;
  const os = enrichedStr(ep, 'DeviceOS') || enrichedStr(ep, 'DeviceOSFamily');
  const t = (key: string) => enrichedBool(ep, key) === true;

  if (t('DeviceIsTV')) return { label: 'Smart TV', os, color: 'warning' };
  if (t('DeviceIsTablet')) return { label: 'Tablet', os, color: 'secondary' };
  if (t('DeviceIsMobilePhone')) return { label: 'Mobile', os, color: 'success' };
  if (t('DeviceIsInAppWebView')) return { label: 'In-app WebView', os, color: 'secondary' };
  if (t('DeviceIsApp') && !t('DeviceIsBrowser')) return { label: 'Native app', os, color: 'info' };
  if (t('DeviceIsBrowser')) return { label: 'Web browser', os, color: 'info' };

  const raw = enrichedStr(ep, 'DeviceType').toLowerCase();
  if (raw.includes('tablet') || raw.includes('ipad')) return { label: 'Tablet', os, color: 'secondary' };
  if (raw.includes('mobile') || raw.includes('phone')) return { label: 'Mobile', os, color: 'success' };
  if (raw.includes('tv') || raw.includes('smarttv') || raw.includes('appletv')) return { label: 'Smart TV', os, color: 'warning' };
  return { label: 'Desktop', os, color: 'info' };
};

const DEVICE_TRAIT_FIELDS: { label: string; prop: string }[] = [
  { label: 'App', prop: 'DeviceIsApp' },
  { label: 'Browser', prop: 'DeviceIsBrowser' },
  { label: 'In-app WebView', prop: 'DeviceIsInAppWebView' },
  { label: 'Mobile phone', prop: 'DeviceIsMobilePhone' },
  { label: 'Tablet', prop: 'DeviceIsTablet' },
  { label: 'TV', prop: 'DeviceIsTV' },
];

const hasAnyDeviceTraitBooleans = (ep: UserSession['EnrichedSessionProperties'] | null | undefined): boolean => {
  if (!ep) return false;
  return DEVICE_TRAIT_FIELDS.some(({ prop }) => isEnrichedBool(enrichedBool(ep, prop)));
};

/** Pill layout for device class chip; use `color="primary"` so theme `MuiChip-colorPrimary` applies. */
const deviceClassChipSx = (compact?: boolean) =>
  ({
    height: compact ? 24 : 28,
    borderRadius: 999,
    fontWeight: 700,
    fontSize: compact ? '0.75rem' : '0.8125rem',
    letterSpacing: '-0.01em',
    '& .MuiChip-label': { px: compact ? 1 : 1.25 },
  }) as const;

const DeviceTraitPills = ({ ep }: { ep: NonNullable<UserSession['EnrichedSessionProperties']> }) => (
  <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mt: 0.5 }}>
    {DEVICE_TRAIT_FIELDS.map(({ label, prop }) => {
      const v = enrichedBool(ep, prop);
      if (!isEnrichedBool(v)) {
        return (
          <Chip
            key={prop}
            label={`${label} · n/a`}
            size="small"
            variant="outlined"
            sx={{
              height: 26,
              borderRadius: 999,
              fontSize: '0.7rem',
              fontWeight: 600,
              borderColor: alpha('#64748b', 0.35),
              color: mon.textMuted,
              bgcolor: alpha('#64748b', 0.06),
            }}
          />
        );
      }
      const on = v === true;
      return on ? (
        <Chip
          key={prop}
          label={label}
          size="small"
          color="primary"
          variant="filled"
          sx={{
            height: 26,
            borderRadius: 999,
            fontSize: '0.7rem',
            fontWeight: 700,
            '& .MuiChip-label': { px: 1.25 },
          }}
        />
      ) : (
        <Chip
          key={prop}
          label={label}
          size="small"
          variant="outlined"
          sx={{
            height: 26,
            borderRadius: 999,
            fontSize: '0.7rem',
            fontWeight: 600,
            color: mon.textMuted,
            borderColor: mon.borderPaper,
            bgcolor: 'transparent',
          }}
        />
      );
    })}
  </Stack>
);

const getWatchContext = (us: UserSession): string => {
  const ep = us.EnrichedSessionProperties;
  if (!ep) return us.UserAgent ?? '—';
  const ua = ep.UserAgent ?? us.UserAgent ?? '';
  if (ep.WatchContextChannelName) return ep.WatchContextChannelName;
  if (ua.includes('Chrome')) return 'Web Browser - Chrome';
  if (ua.includes('Firefox')) return 'Web Browser - Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Web Browser - Safari';
  if (ua.includes('EdgA') || ua.includes('Edg/')) return 'Web Browser - Edge';
  if (ep.ApplicationName) return ep.ApplicationName;
  return ua ? ua.substring(0, 40) : '—';
};

const truncateId = (id: string, len = 14) => (id.length > len ? `${id.substring(0, len)}…` : id);

// ─── Session Type Chip ────────────────────────────────────────────────────────

const SessionTypeChip = ({ isShared }: { isShared: boolean }) =>
  isShared ? (
    <Chip
      label="Fast"
      size="small"
      sx={{ fontWeight: 700, bgcolor: '#7c3aed', color: 'white', border: 'none' }}
    />
  ) : (
    <Chip
      label="Standard"
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, color: mon.textMuted, borderColor: mon.borderPaper }}
    />
  );

// ─── OpenStreetMap Embed ──────────────────────────────────────────────────────

const GeoMap = ({ lat, lon }: { lat: number; lon: number }) => {
  const delta = 0.15;
  const bbox = `${lon - delta},${lat - delta},${lon + delta},${lat + delta}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 180,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: mon.borderPaper,
      }}
    >
      <iframe
        title="Location map"
        src={src}
        width="100%"
        height="100%"
        style={{ border: 0, display: 'block' }}
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </Box>
  );
};

// ─── Trace Message Item ───────────────────────────────────────────────────────

interface TraceMessageItemProps {
  msg: Record<string, unknown>;
  isNew?: boolean;
}

const TraceMessageItem = ({ msg, isNew }: TraceMessageItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const messageType = (get(msg, 'messageType') ?? get(msg, 'MessageType')) as string;
  const data = (get(msg, 'data') ?? get(msg, 'Data') ?? get(msg, 'eventData') ?? get(msg, 'EventData') ?? msg) as Record<string, unknown>;
  const message = get(msg, 'message') ?? get(msg, 'Message');
  const eventType = data ? ((get(data, 'eventType') ?? get(data, 'EventType')) as string) : undefined;
  const timestamp = extractTimestamp(msg);

  const displayMessage =
    message != null && typeof message !== 'object'
      ? String(message)
      : message != null
        ? JSON.stringify(message)
        : typeof msg === 'object'
          ? JSON.stringify(msg)
          : String(msg);

  const IconWrapper = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Tooltip title={title}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {children}
      </Box>
    </Tooltip>
  );

  let icon: React.ReactNode;
  if (messageType === MessageType.ConnectToTopic) {
    icon = (
      <IconWrapper title="Connect to Topic">
        <LinkIcon sx={{ color: 'success.main', fontSize: 16 }} />
      </IconWrapper>
    );
  } else if (eventType === 'TelemetryEvent') {
    icon = (
      <IconWrapper title="Telemetry Event">
        <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 16 }} />
      </IconWrapper>
    );
  } else if (eventType === 'AnalyticsEvent') {
    icon = (
      <IconWrapper title="Analytics Event">
        <AnalyticsIcon sx={{ color: 'success.main', fontSize: 16 }} />
      </IconWrapper>
    );
  } else {
    icon = (
      <IconWrapper title="Event">
        <InfoOutlinedIcon sx={{ color: mon.iconMuted, fontSize: 16 }} />
      </IconWrapper>
    );
  }

  const borderColor =
    messageType === MessageType.ConnectToTopic
      ? 'success.main'
      : eventType === 'TelemetryEvent'
        ? 'info.main'
        : eventType === 'AnalyticsEvent'
          ? 'success.main'
          : mon.borderPaper;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        overflow: 'hidden',
        borderRadius: 1,
        borderColor: mon.borderPaper,
        borderLeft: '4px solid',
        borderLeftColor: borderColor,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          ...(isNew
            ? {
              animation: 'traceHighlight 2s ease-out forwards',
              '@keyframes traceHighlight': {
                '0%': { backgroundColor: 'rgba(2, 136, 209, 0.2)' },
                '100%': { backgroundColor: 'transparent' },
              },
            }
            : {}),
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        <Box sx={{ flexShrink: 0, mt: 0.15 }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', flex: 1 }}>
              {displayMessage}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {timestamp && (
                <Typography variant="caption" sx={{ color: mon.textMuted }}>
                  {timestamp}
                </Typography>
              )}
              <ChevronRightIcon
                sx={{
                  fontSize: 14,
                  color: mon.iconMuted,
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '0.72rem',
              fontFamily: 'monospace',
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {JSON.stringify(msg, null, 2)}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Live Tracing Panel ───────────────────────────────────────────────────────

interface LiveTracingPanelProps {
  enabled: boolean;
  onToggle: () => void;
  patching: boolean;
  disabled?: boolean;
  messages: unknown[];
  prevCountRef: React.MutableRefObject<number | null>;
  socketReady: boolean;
}

const LiveTracingPanel = ({
  enabled,
  onToggle,
  patching,
  disabled,
  messages,
  prevCountRef,
  socketReady,
}: LiveTracingPanelProps) => {
  const newCount =
    prevCountRef.current === null ? 0 : Math.max(0, messages.length - prevCountRef.current);
  prevCountRef.current = messages.length;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: mon.borderPaper }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GraphicEqIcon sx={{ color: 'info.main', fontSize: 20 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: mon.textSection }}>
                Live Tracing
              </Typography>
              {enabled && (
                <>
                  {/* Pulsing dot */}
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: socketReady ? 'success.main' : 'warning.main',
                      animation: 'liveTracingPulse 2.5s ease-in-out infinite',
                      '@keyframes liveTracingPulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.25 },
                      },
                    }}
                  />
                  {messages.length > 0 && (
                    <Chip
                      label={`${messages.length} event${messages.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="success"
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                    />
                  )}
                </>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: mon.textMuted }}>
              Listen for real-time events from the streaming session
            </Typography>
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={onToggle}
              disabled={patching || disabled}
              size="small"
            />
          }
          label={
            <Typography variant="caption" fontWeight={500} sx={{ color: mon.textMuted }}>
              {enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          }
          labelPlacement="start"
          sx={{ mr: 0 }}
        />
      </Box>

      {enabled && (
        <>
          <Divider sx={{ borderColor: mon.divider }} />
          <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
            {messages.length === 0 ? (
              <Typography variant="body2" sx={{ py: 0.5, color: mon.textMuted }}>
                {socketReady ? 'Waiting for messages…' : 'Connecting…'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[...messages].reverse().map((msg, idx) => (
                  <TraceMessageItem
                    key={messages.length - 1 - idx}
                    msg={
                      typeof msg === 'object' && msg !== null
                        ? (msg as Record<string, unknown>)
                        : { message: String(msg) }
                    }
                    isNew={idx < newCount}
                  />
                ))}
              </Box>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

// ─── Session List View ────────────────────────────────────────────────────────

interface SessionListViewProps {
  sessions: StreamingEngineSession[];
  loading: boolean;
  onSelectSession: (session: StreamingEngineSession) => void;
  onRefresh: () => void;
  timezone: string;
}

const SessionListView = ({ sessions, loading, onSelectSession, onRefresh, timezone }: SessionListViewProps) => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <GraphicEqIcon sx={{ color: 'primary.main', fontSize: 26 }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: mon.textSection }}>
              Stream Monitoring Dashboard
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: mon.textMuted }}>
            Monitor and manage real-time streaming sessions for your channel
          </Typography>
        </Box>
        <Tooltip title="Refresh sessions">
          <span>
            <IconButton onClick={onRefresh} disabled={loading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {!loading && sessions.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ color: mon.textMuted }}>
            No sessions found for this channel.
          </Typography>
        </Box>
      )}
      {!loading && sessions.length > 0 && (
        <Box>
          {/* Column header row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 1.6fr 0.8fr 1.1fr auto',
              gap: 2,
              px: 2.5,
              pb: 0.75,
              alignItems: 'center',
            }}
          >
            <Typography variant="caption" fontWeight={600} sx={{ color: mon.textMuted }}>
              Session ID
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ color: mon.textMuted }}>
              Start Date/Time
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ color: mon.textMuted }}>
              Status
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ color: mon.textMuted }}>
              Current Duration
            </Typography>
            <Box sx={{ minWidth: 72 }} />
          </Box>

          {sessions.map((session) => {
            const sessionId = getSessionId(session);
            const started = getStartedDate(session);
            const stopped = getStoppedDate(session);
            const running = isSessionRunning(session);
            const isShared = session.isSharedSession ?? asRecord(session).IsSharedSession ?? false;

            void tick;
            const duration = formatDuration(started, running ? undefined : stopped);

            return (
              <Card
                key={sessionId}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  borderRadius: 2,
                  borderColor: mon.borderPaper,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <CardActionArea onClick={() => onSelectSession(session)} sx={{ p: 0 }}>
                  <CardContent sx={{ py: 1.75, px: 2.5, '&:last-child': { pb: 1.75 } }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1.6fr 0.8fr 1.1fr auto',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      {/* Session ID */}
                      <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-all' }}>
                        {sessionId}
                      </Typography>

                      {/* Start Date/Time */}
                      <Typography variant="body2" fontWeight={600}>
                        {formatDateInline(started, timezone)}
                      </Typography>

                      {/* Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {running ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: mon.iconFaint }} />
                        )}
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: running ? 'success.main' : mon.textMuted }}
                        >
                          {running ? 'Running' : 'Stopped'}
                        </Typography>
                      </Box>

                      {/* Duration */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: mon.iconMuted }} />
                        <Typography variant="body2" fontWeight={600}>
                          {duration}
                        </Typography>
                      </Box>

                      {/* Session type pill */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 72 }}>
                        <SessionTypeChip isShared={!!isShared} />
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Session Detail View ──────────────────────────────────────────────────────

interface SessionDetailViewProps {
  session: StreamingEngineSession;
  sessionState: StreamingEngineSessionState | null;
  detailLoading: boolean;
  userSessions: UserSession[];
  userSessionsLoading: boolean;
  liveTracingEnabled: boolean;
  liveTracingPatching: boolean;
  isSessionStopped: boolean;
  traceMessages: unknown[];
  socketReady: boolean;
  onBack: () => void;
  onToggleLiveTracing: () => void;
  onResetSession: () => void;
  resetting: boolean;
  onSelectUserSession: (us: UserSession) => void;
  onRefreshUserSessions: () => void;
  prevTraceCountRef: React.MutableRefObject<number | null>;
  timezone: string;
}

const SessionDetailView = ({
  session,
  sessionState,
  detailLoading,
  userSessions,
  userSessionsLoading,
  liveTracingEnabled,
  liveTracingPatching,
  isSessionStopped,
  traceMessages,
  socketReady,
  onBack,
  onToggleLiveTracing,
  onResetSession,
  resetting,
  onSelectUserSession,
  onRefreshUserSessions,
  prevTraceCountRef,
  timezone,
}: SessionDetailViewProps) => {
  const sessionId = getSessionId(session);
  const isShared =
    sessionState?.isSharedSession ??
    session.isSharedSession ??
    asRecord(session).IsSharedSession ??
    false;

  const started = sessionState ? getStartedDate(sessionState) : getStartedDate(session);
  const stopped = sessionState ? getStoppedDate(sessionState) : getStoppedDate(session);
  const running = sessionState ? isSessionRunning(sessionState) : isSessionRunning(session);
  const variants = sessionState?.variants ?? (session as StreamingEngineSession).variants ?? [];

  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  void tick;
  const duration = formatDuration(started, running ? undefined : stopped);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Back navigation */}
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={onBack}
          sx={{ mb: 1, color: mon.textMuted, '&:hover': { color: mon.textPrimary } }}
        >
          Back to Sessions
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: mon.textSection }}>
              Session Details
            </Typography>
            <Typography variant="body2" sx={{ color: mon.textMuted }}>
              {sessionId}
            </Typography>
          </Box>
          {/* Both chips use size="small" with no explicit height — same size */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, mt: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={resetting ? <CircularProgress size={14} /> : <RestartAltIcon />}
              onClick={onResetSession}
              disabled={resetting || detailLoading}
            >
              Reset Session
            </Button>
            <SessionTypeChip isShared={!!isShared} />
            <Chip
              icon={
                running ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />
                )
              }
              label={running ? 'Running' : 'Stopped'}
              color={running ? 'success' : 'default'}
              size="small"
              sx={{ fontWeight: 700 }}
            />
          </Box>
        </Box>
      </Box>

      {detailLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!detailLoading && (
        <>
          {/* Session Information card */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: mon.borderPaper }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: mon.textSection }}>
              Session Information
            </Typography>
            <Grid container spacing={3} alignItems="flex-start">
              {/* Started — date and time on the same line */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <CalendarTodayIcon sx={{ fontSize: 20, color: mon.iconMuted, mt: 0.25, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="caption" display="block" sx={{ mb: 0.25, color: mon.textMuted }}>
                      Started
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDateInline(started, timezone)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Duration */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <AccessTimeIcon sx={{ fontSize: 20, color: mon.iconMuted, mt: 0.25, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="caption" display="block" sx={{ mb: 0.25, color: mon.textMuted }}>
                      Duration
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {duration}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Variants */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <LayersIcon sx={{ fontSize: 20, color: mon.iconMuted, mt: 0.25, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="caption" display="block" sx={{ mb: 0.5, color: mon.textMuted }}>
                      Variants
                    </Typography>
                    {variants.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {variants.map((v) => (
                          <Chip
                            key={v}
                            label={v}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem', borderColor: mon.borderPaper, color: mon.textMuted }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: mon.textMuted }}>
                        —
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Active User Sessions */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: mon.borderPaper }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5 }}>
              <PeopleAltIcon sx={{ color: 'info.main', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: mon.textSection }}>
                Active User Sessions
              </Typography>
              {!userSessionsLoading && (
                <Chip
                  label={`${userSessions.length} viewer${userSessions.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="info"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              <Box sx={{ ml: 'auto' }}>
                <Tooltip title="Refresh user sessions">
                  <span>
                    <IconButton
                      size="small"
                      onClick={onRefreshUserSessions}
                      disabled={userSessionsLoading}
                    >
                      {userSessionsLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RefreshIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ borderColor: mon.divider }} />
            {userSessionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : userSessions.length === 0 ? (
              <Box sx={{ py: 3, px: 2.5 }}>
                <Typography variant="body2" sx={{ color: mon.textMuted }}>
                  No active user sessions found.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>User Session ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Session Start</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>IP Address</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Device</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Watch Context</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...userSessions]
                      .sort((a, b) => new Date(b.SessionStartDateUtc).getTime() - new Date(a.SessionStartDateUtc).getTime())
                      .map((us) => {
                        const device = getDeviceInfo(us);
                        const ep = us.EnrichedSessionProperties;
                        const location = [ep?.GeoRegionName, ep?.GeoCountryName].filter(Boolean).join(', ') || '—';
                        const watchContext = getWatchContext(us);
                        return (
                          <TableRow
                            key={us.UserSessionDocumentId || us.UserSessionId}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => onSelectUserSession(us)}
                          >
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              <Tooltip title={us.UserSessionId}>
                                <span>{truncateId(us.UserSessionId)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                              {formatDate(us.SessionStartDateUtc, timezone)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {us.ClientIp ?? '—'}
                            </TableCell>
                            <TableCell sx={{ py: 0.25 }}>
                              <Chip label={device.label} size="small" color="primary" sx={deviceClassChipSx(true)} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {location}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: mon.textMuted }}>
                              {watchContext}
                            </TableCell>
                            <TableCell sx={{ pr: 1 }}>
                              <ChevronRightIcon sx={{ fontSize: 16, color: mon.iconMuted }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Live Tracing — at the bottom */}
          <LiveTracingPanel
            enabled={liveTracingEnabled}
            onToggle={onToggleLiveTracing}
            patching={liveTracingPatching}
            disabled={isSessionStopped}
            messages={traceMessages}
            prevCountRef={prevTraceCountRef}
            socketReady={socketReady}
          />
        </>
      )}
    </Box>
  );
};

// ─── User Session Detail View ─────────────────────────────────────────────────

interface UserSessionDetailViewProps {
  userSession: UserSession;
  sessionId: string;
  onBack: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  timezone: string;
  userSessionExpired: boolean;
  onUserSessionUpdated: (us: UserSession) => void;
}

const UserSessionDetailView = ({
  userSession: us,
  sessionId,
  onBack,
  onRefresh,
  refreshing,
  timezone,
  userSessionExpired,
  onUserSessionUpdated,
}: UserSessionDetailViewProps) => {
  const { alert } = useAppUtils();
  const alertRef = useRef(alert);
  alertRef.current = alert;
  const { globalData } = useGlobalData();

  const liveTracingEnabled = us.EnableLiveTracing ?? false;
  const [patching, setPatching] = useState(false);
  const [traceMessages, setTraceMessages] = useState<unknown[]>([]);
  const traceMessagesRef = useRef<unknown[]>([]);
  const prevCountRef = useRef<number | null>(null);

  const socketUrl = globalData?.notificationServiceWebSocketUrl || '';
  const shouldConnect = liveTracingEnabled && !!socketUrl && !userSessionExpired;

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    socketUrl,
    {
      onOpen: () => {
        const token = LocalStorageService.getItem('ACCESS_TOKEN');
        sendJsonMessage({
          action: WebSocketActionType.connectToTopic,
          topicId: `SES-USERSESSION:${us.UserSessionId}`,
          accessToken: token,
        });
      },
      shouldReconnect: () => shouldConnect,
    },
    shouldConnect
  );

  useEffect(() => {
    if (lastJsonMessage != null && typeof lastJsonMessage === 'object' && !Array.isArray(lastJsonMessage)) {
      const raw = lastJsonMessage as Record<string, unknown>;
      if (isKeepAliveWebSocketPayload(raw)) return;
      const msgWithTs = { ...raw, _receivedAt: new Date().toISOString() };
      traceMessagesRef.current = [...traceMessagesRef.current, msgWithTs];
      setTraceMessages([...traceMessagesRef.current]);
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    if (!shouldConnect || readyState !== ReadyState.OPEN) return undefined;
    const id = setInterval(() => {
      try {
        sendJsonMessage({ action: WebSocketActionType.keepAlive });
      } catch (e) {
        console.error('keepAlive error', e);
      }
    }, 20000);
    return () => clearInterval(id);
  }, [shouldConnect, readyState, sendJsonMessage]);

  const handleToggle = async () => {
    setPatching(true);
    try {
      const newVal = !liveTracingEnabled;
      await StreamingEngineService.setUserSessionLiveTracing(sessionId, us.UserSessionDocumentId, newVal);
      const data = await StreamingEngineService.getUserSession(sessionId, us.UserSessionDocumentId);
      onUserSessionUpdated(data);
      if (newVal) {
        alertRef.current('User session live tracing enabled. It may take up to 1 minute to see events.', 'info');
      }
    } catch (err) {
      console.error('Failed to toggle user session live tracing', err);
      alertRef.current('Failed to update user session live tracing.', 'error');
    } finally {
      setPatching(false);
    }
  };

  const ep = us.EnrichedSessionProperties;
  const usRec = asRecord(us);
  const pb = {
    totalAssets: safeNum(get(usRec, 'TotalAssetsPlayedCount')),
    totalAssetsDuration: safeNum(get(usRec, 'TotalAssetsPlayedDurationSeconds')),
    contentCount: safeNum(get(usRec, 'TotalContentPlayedCount')),
    contentDuration: safeNum(get(usRec, 'TotalContentPlayedDurationSeconds')),
    liveCount: safeNum(get(usRec, 'TotalLivePlayedCount')),
    liveDuration: safeNum(get(usRec, 'TotalLivePlayedDurationSeconds')),
    adsCount: safeNum(get(usRec, 'TotalAdsPlayedCount')),
    adsDuration: safeNum(get(usRec, 'TotalAdsPlayedDurationSeconds')),
    adFillerCount: safeNum(get(usRec, 'TotalAdFillerPlayedCount')),
    adFillerDuration: safeNum(get(usRec, 'TotalAdFillerPlayedDurationSeconds')),
    backstopCount: safeNum(get(usRec, 'TotalBackstopPlayedCount')),
    backstopDuration: safeNum(get(usRec, 'TotalBackstopPlayedDurationSeconds')),
    fillerCount: safeNum(get(usRec, 'TotalFillerPlayedCount')),
    fillerDuration: safeNum(get(usRec, 'TotalFillerPlayedDurationSeconds')),
  };
  const hasPlaybackSection = Object.values(pb).some((v) => v != null);

  const playbackBreakdownItems = [
    {
      key: 'vod',
      title: 'VOD',
      count: pb.contentCount,
      durationSeconds: pb.contentDuration,
      icon: <MovieCreationOutlinedIcon />,
    },
    {
      key: 'live',
      title: 'Live',
      count: pb.liveCount,
      durationSeconds: pb.liveDuration,
      icon: <LiveTvOutlinedIcon />,
    },
    {
      key: 'ads',
      title: 'Ads',
      count: pb.adsCount,
      durationSeconds: pb.adsDuration,
      icon: <CampaignOutlinedIcon />,
    },
    {
      key: 'adFiller',
      title: 'Ad Filler',
      count: pb.adFillerCount,
      durationSeconds: pb.adFillerDuration,
      icon: <ViewStreamOutlinedIcon />,
    },
    {
      key: 'backstop',
      title: 'Backstop',
      count: pb.backstopCount,
      durationSeconds: pb.backstopDuration,
      icon: <ShieldOutlinedIcon />,
    },
    {
      key: 'filler',
      title: 'Filler',
      count: pb.fillerCount,
      durationSeconds: pb.fillerDuration,
      icon: <ViewColumnOutlinedIcon />,
    },
  ].filter((item) => item.count != null || item.durationSeconds != null);

  const playbackTotalCount =
    pb.totalAssets ??
    (playbackBreakdownItems.length > 0
      ? playbackBreakdownItems.reduce((sum, item) => sum + (item.count ?? 0), 0)
      : undefined);
  const playbackTotalDuration =
    pb.totalAssetsDuration ??
    (playbackBreakdownItems.length > 0
      ? playbackBreakdownItems.reduce((sum, item) => sum + (item.durationSeconds ?? 0), 0)
      : undefined);

  const device = getDeviceInfo(us);
  const hasLocation = ep?.GeoLatitude != null && ep?.GeoLongitude != null;
  const deviceOs = enrichedStr(ep, 'DeviceOS');
  const deviceOsFamily = enrichedStr(ep, 'DeviceOSFamily');
  const deviceTypeRaw = enrichedStr(ep, 'DeviceType');

  const DeviceIcon =
    device.label === 'Mobile'
      ? PhoneAndroidIcon
      : device.label === 'Tablet'
        ? TabletIcon
        : device.label === 'In-app WebView' || device.label === 'Web browser'
          ? OpenInBrowserIcon
          : device.label === 'Smart TV'
            ? TvIcon
            : device.label === 'Native app'
              ? LayersIcon
              : DesktopMacIcon;

  const userSessionDuration = formatDuration(us.SessionStartDateUtc, us.LastSegmentPlayedDateTimeUtc, {
    fallBackToNow: false,
  });

  // Watch Context section fields
  const watchContextFields: { label: string; value: string | null | undefined }[] = [
    { label: 'Application Name', value: ep?.ApplicationName },
    { label: 'Application Bundle', value: ep?.ApplicationBundle },
    { label: 'Channel Name', value: ep?.WatchContextChannelName },
    { label: 'Channel Type', value: ep?.WatchContextChannelType },
    { label: 'Referrer Type', value: ep?.WatchContextReferrerType },
    { label: 'Referrer ID', value: ep?.WatchContextReferrerId },
  ].filter((f) => f.value != null && f.value !== '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Back */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={onBack}
            sx={{ color: mon.textMuted, '&:hover': { color: mon.textPrimary } }}
          >
            Back to Session
          </Button>
          <Tooltip title="Refresh user session">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={refreshing || userSessionExpired}>
                {refreshing ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: mon.textSection, mb: 0.25 }}>
            User Session
          </Typography>
          <Typography variant="body2" sx={{ color: mon.textMuted }}>
            {us.UserSessionId}
          </Typography>
        </Box>
      </Box>

      {/* Session Timing */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: mon.borderPaper }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: mon.iconMuted }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: mon.textSection }}>
            Session Information
          </Typography>
        </Box>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <CalendarTodayIcon sx={{ fontSize: 20, color: mon.iconMuted, mt: 0.25, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" display="block" sx={{ mb: 0.25, color: mon.textMuted }}>
                  Started
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatDateInline(us.SessionStartDateUtc, timezone)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <AccessTimeIcon sx={{ fontSize: 20, color: mon.iconMuted, mt: 0.25, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" display="block" sx={{ mb: 0.25, color: mon.textMuted }}>
                  Duration
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {userSessionDuration}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <MovieOutlinedIcon sx={{ fontSize: 20, color: mon.iconMuted, mt: 0.25, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" display="block" sx={{ mb: 0.25, color: mon.textMuted }}>
                  Last segment played
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatDateInline(us.LastSegmentPlayedDateTimeUtc, timezone)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {ep?.UserId && (
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <PersonOutlineIcon sx={{ fontSize: 20, color: mon.iconMuted, mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" display="block" sx={{ mb: 0.25, color: mon.textMuted }}>
                    User ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {ep.UserId}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

        </Grid>
      </Paper>

      {hasPlaybackSection && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: mon.borderPaper }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box
              className="cm-playback-header-icon"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AnalyticsIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: mon.textSection, lineHeight: 1.2 }}>
                Playback Activity
              </Typography>
              <Typography variant="caption" sx={{ color: mon.textMuted, display: 'block' }}>
                VOD, live, ads, ad filler, backstop, and filler served in this session
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
              width: '100%',
              minHeight: 200,
              px: 0.5,
              py: 1,
            }}
          >
            <PlaybackTotalSummary count={playbackTotalCount ?? null} durationSeconds={playbackTotalDuration ?? null} />
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', md: 'block' },
                mx: 2.5,
                my: 0.5,
                borderColor: mon.divider,
              }}
            />
            <Divider sx={{ display: { xs: 'block', md: 'none' }, my: 2, borderColor: mon.divider }} />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(6, minmax(0, 1fr))',
                },
                gap: 1.5,
                alignContent: 'start',
              }}
            >
              {playbackBreakdownItems.map((item) => (
                <PlaybackBreakdownTile
                  key={item.key}
                  title={item.title}
                  count={item.count ?? null}
                  durationSeconds={item.durationSeconds ?? null}
                  variant={item.key}
                  icon={item.icon}
                />
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Device */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: mon.borderPaper }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DeviceIcon sx={{ fontSize: 18, color: mon.iconMuted }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: mon.textSection }}>
            Device
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ rowGap: 1 }}>
              <Chip label={device.label} size="small" color="primary" sx={deviceClassChipSx()} />
              {device.os ? (
                <Typography variant="body2" component="span" fontWeight={600} sx={{ color: mon.textPrimary }}>
                  {device.os}
                </Typography>
              ) : null}
              {deviceOs && deviceOsFamily && deviceOsFamily !== deviceOs ? (
                <Typography variant="caption" component="span" sx={{ color: mon.textMuted }}>
                  ({deviceOsFamily})
                </Typography>
              ) : null}
              {deviceTypeRaw ? (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 1,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '0.7rem',
                    color: mon.textMuted,
                    bgcolor: alpha('#0f172a', 0.06),
                    border: `1px solid ${alpha('#0f172a', 0.08)}`,
                    wordBreak: 'break-all',
                    maxWidth: '100%',
                  }}
                >
                  {deviceTypeRaw}
                </Typography>
              ) : null}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
              IP address
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {us.ClientIp || '—'}
            </Typography>
          </Grid>
          {hasAnyDeviceTraitBooleans(ep) && ep ? (
            <Grid item xs={12}>
              <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                Client environment
              </Typography>
              <DeviceTraitPills ep={ep} />
            </Grid>
          ) : null}
          {(us.UserAgent || enrichedStr(ep, 'UserAgent')) && (
            <Grid item xs={12}>
              <Typography variant="caption" display="block" sx={{ color: mon.textMuted, mb: 0.75 }}>
                User agent
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 2,
                  fontSize: '0.72rem',
                  lineHeight: 1.5,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  color: mon.textSection,
                  bgcolor: alpha('#0f172a', 0.045),
                  border: `1px solid ${mon.borderPaper}`,
                }}
              >
                {us.UserAgent || enrichedStr(ep, 'UserAgent')}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Location */}
      {(ep?.GeoCountryName || ep?.GeoRegionName || hasLocation) && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: mon.borderPaper }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocationOnIcon sx={{ fontSize: 18, color: mon.iconMuted }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: mon.textSection }}>
              Location
            </Typography>
          </Box>
          {/* 3-column layout: fields cols 1-2, map col 3 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 2, alignItems: 'start' }}>
            {/* Column 1 & 2: address fields */}
            <Box sx={{ gridColumn: '1 / 3', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {ep?.GeoCityName && (
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                    City
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ep.GeoCityName}
                  </Typography>
                </Box>
              )}
              {ep?.GeoRegionName && (
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                    State
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ep.GeoRegionName}
                  </Typography>
                </Box>
              )}
              {ep?.GeoZip && (
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                    ZIP Code
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ep.GeoZip}
                  </Typography>
                </Box>
              )}
              {ep?.GeoCountryName && (
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                    Country
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ep.GeoCountryName}
                  </Typography>
                </Box>
              )}
              {ep?.GeoTimeZoneId && (
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                    Timezone
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ep.GeoTimeZoneId}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Column 3: map */}
            {hasLocation && (
              <Box sx={{ gridColumn: '3', alignSelf: 'stretch', minHeight: 180 }}>
                <GeoMap lat={ep.GeoLatitude as number} lon={ep.GeoLongitude as number} />
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Watch Context */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: mon.borderPaper }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TvIcon sx={{ fontSize: 18, color: mon.iconMuted }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: mon.textSection }}>
            Watch Context
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {watchContextFields.map((f) => (
            <Grid item xs={12} sm={6} key={f.label}>
              <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                {f.label}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                {f.value}
              </Typography>
            </Grid>
          ))}
        </Grid>
        {watchContextFields.length === 0 && (
          <Typography variant="body2" sx={{ color: mon.textMuted }}>
            No watch context data available.
          </Typography>
        )}
      </Paper>

      {/* Request Parameters */}
      {ep?.QueryStringParameters != null && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: mon.borderPaper }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <TuneIcon sx={{ fontSize: 18, color: mon.iconMuted }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: mon.textSection }}>
              Request Parameters
            </Typography>
            <Chip
              label={Object.keys(ep.QueryStringParameters).length}
              size="small"
              sx={{ height: 18, fontSize: '0.68rem', ml: 0.5 }}
            />
          </Box>
          {Object.keys(ep.QueryStringParameters).length === 0 ? (
            <Typography variant="body2" sx={{ color: mon.textMuted }}>
              No query string parameters.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {Object.entries(ep.QueryStringParameters)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, value]) => (
                  <Box key={key} sx={{ minWidth: 0 }}>
                    <Typography variant="caption" display="block" sx={{ color: mon.textMuted }}>
                      {key}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                      {value || <em style={{ opacity: 0.5 }}>empty</em>}
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Live Tracing */}
      <LiveTracingPanel
        enabled={liveTracingEnabled}
        onToggle={handleToggle}
        patching={patching}
        disabled={userSessionExpired}
        messages={traceMessages}
        prevCountRef={prevCountRef}
        socketReady={readyState === ReadyState.OPEN}
      />
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface ChannelMonitoringProps {
  channelId: string;
}

const ChannelMonitoring = ({ channelId }: ChannelMonitoringProps) => {
  const { globalData } = useGlobalData();
  const { ouData } = useSelector(applicationDataSelector);
  const { alert, vnConfirm } = useAppUtils();
  const alertRef = useRef(alert);
  alertRef.current = alert;

  const timezone = ouData?.timezone || 'UTC';

  // ── View state ──
  const [view, setView] = useState<MonitoringView>('list');
  const [selectedSession, setSelectedSession] = useState<StreamingEngineSession | null>(null);
  const [selectedUserSession, setSelectedUserSession] = useState<UserSession | null>(null);
  const [userSessionRefreshing, setUserSessionRefreshing] = useState(false);
  /** User session GET returned 404 — stop polling and disable actions until user navigates away. */
  const [userSessionExpired, setUserSessionExpired] = useState(false);

  // ── Session list ──
  const [sessions, setSessions] = useState<StreamingEngineSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [resettingSessionId, setResettingSessionId] = useState<string | null>(null);

  // ── Session detail ──
  const [sessionState, setSessionState] = useState<StreamingEngineSessionState | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sessionNotFoundError, setSessionNotFoundError] = useState(false);

  // ── User sessions ──
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const [userSessionsLoading, setUserSessionsLoading] = useState(false);

  // ── Session-level live tracing ──
  const [traceMessages, setTraceMessages] = useState<unknown[]>([]);
  const [liveTracingPatching, setLiveTracingPatching] = useState(false);
  const traceMessagesRef = useRef<unknown[]>([]);
  const prevTraceCountRef = useRef<number | null>(null);

  const socketUrl = globalData?.notificationServiceWebSocketUrl || '';
  const selectedSessionId = selectedSession ? getSessionId(selectedSession) : null;
  const serverLiveTracingEnabled = sessionState?.enableLiveTracing ?? false;
  const shouldConnect = serverLiveTracingEnabled && !!socketUrl && !!selectedSessionId;

  const stoppedDate = sessionState?.sessionStoppedDateUtc;
  const isSessionStopped = !!stoppedDate && !stoppedDate.startsWith('0001-01-01');

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    socketUrl,
    {
      onOpen: () => {
        if (selectedSessionId) {
          const token = LocalStorageService.getItem('ACCESS_TOKEN');
          sendJsonMessage({
            action: WebSocketActionType.connectToTopic,
            topicId: `SES-SESSION:${selectedSessionId}`,
            accessToken: token,
          });
        }
      },
      shouldReconnect: () => shouldConnect,
    },
    shouldConnect
  );

  useEffect(() => {
    if (lastJsonMessage != null && typeof lastJsonMessage === 'object' && !Array.isArray(lastJsonMessage)) {
      const raw = lastJsonMessage as Record<string, unknown>;
      if (isKeepAliveWebSocketPayload(raw)) return;
      const msg = { ...raw, _receivedAt: new Date().toISOString() };
      traceMessagesRef.current = [...traceMessagesRef.current, msg];
      setTraceMessages([...traceMessagesRef.current]);
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    if (!shouldConnect || readyState !== ReadyState.OPEN) return undefined;
    const id = setInterval(() => {
      try {
        sendJsonMessage({ action: WebSocketActionType.keepAlive });
      } catch (e) {
        console.error('keepAlive error', e);
      }
    }, 20000);
    return () => clearInterval(id);
  }, [shouldConnect, readyState, sendJsonMessage]);

  // ── Load sessions ──
  const loadSessions = useCallback(async () => {
    if (!channelId || clientUtils.isNullOrWhitespace(channelId)) return;
    setLoading(true);
    try {
      const data = await StreamingEngineService.getChannelSessions(channelId);
      const sorted = [...data].sort((a, b) => {
        const aStart = String(a.sessionStartedDateUtc ?? asRecord(a).SessionStartedDateUtc ?? '');
        const bStart = String(b.sessionStartedDateUtc ?? asRecord(b).SessionStartedDateUtc ?? '');
        return bStart.localeCompare(aStart);
      });
      setSessions(sorted);
    } catch (err) {
      console.error('Failed to load sessions', err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message =
        status === 200
          ? 'Received an unexpected response format from the Streaming Engine Service.'
          : 'Failed to load channel sessions. The Streaming Engine Service may not be configured.';
      alertRef.current(message, 'error');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // ── Fetch session state ──
  const fetchSessionState = useCallback((sessionId: string, isInitialLoad: boolean) => {
    if (isInitialLoad) {
      setDetailLoading(true);
      setSessionState(null);
    }
    StreamingEngineService.getSessionState(sessionId)
      .then((state) => {
        setSessionNotFoundError(false);
        setSessionState(state);
      })
      .catch((err) => {
        console.error('Failed to load session state', err);
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setSessionState(null);
          setSessionNotFoundError(true);
          alertRef.current(`Unable to load session ${sessionId}: Session may no longer exist`, 'error');
        } else {
          alertRef.current('Failed to load session details.', 'error');
        }
      })
      .finally(() => {
        if (isInitialLoad) setDetailLoading(false);
      });
  }, []);

  // ── Polling for session state ──
  const refreshIntervalMs = Math.max(
    1000,
    (sessionState?.desiredContentSeconds ?? sessionState?.targetDuration ?? 6) * 1000
  );

  useEffect(() => {
    if (view !== 'sessionDetail' || !selectedSessionId || !sessionState || sessionNotFoundError) {
      return undefined;
    }
    const id = setInterval(() => {
      fetchSessionState(selectedSessionId, false);
    }, refreshIntervalMs);
    return () => clearInterval(id);
  }, [view, selectedSessionId, sessionState, sessionNotFoundError, refreshIntervalMs, fetchSessionState]);

  // ── Poll user session detail (same interval as session state) ──
  useEffect(() => {
    if (
      view !== 'userSessionDetail' ||
      !selectedSessionId ||
      !selectedUserSession?.UserSessionDocumentId ||
      userSessionExpired
    ) {
      return undefined;
    }
    const userSessionDocumentId = selectedUserSession.UserSessionDocumentId;
    const id = setInterval(() => {
      StreamingEngineService.getUserSession(selectedSessionId, userSessionDocumentId)
        .then((data) => setSelectedUserSession(data))
        .catch((err) => {
          console.error('Failed to poll user session', err);
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404) {
            setUserSessionExpired(true);
            alertRef.current('This user session no longer exists or has expired.', 'warning');
          }
        });
    }, refreshIntervalMs);
    return () => clearInterval(id);
  }, [
    view,
    selectedSessionId,
    selectedUserSession?.UserSessionDocumentId,
    userSessionExpired,
    refreshIntervalMs,
  ]);

  // ── Fetch / refresh user sessions ──
  const fetchUserSessions = useCallback(
    (sessionId: string) => {
      setUserSessionsLoading(true);
      StreamingEngineService.getUserSessions(sessionId)
        .then((data) => setUserSessions(data))
        .catch((err) => {
          console.error('Failed to load user sessions', err);
          alertRef.current('Failed to load user sessions.', 'error');
        })
        .finally(() => setUserSessionsLoading(false));
    },
    []
  );

  // ── Navigate to session detail ──
  const handleSelectSession = useCallback(
    (session: StreamingEngineSession) => {
      const sessionId = getSessionId(session);
      setSelectedSession(session);
      setUserSessionExpired(false);
      setSessionState(null);
      setUserSessions([]);
      traceMessagesRef.current = [];
      setTraceMessages([]);
      prevTraceCountRef.current = null;
      setSessionNotFoundError(false);
      setView('sessionDetail');

      fetchSessionState(sessionId, true);
      fetchUserSessions(sessionId);
    },
    [fetchSessionState, fetchUserSessions]
  );

  // ── Navigate to user session detail ──
  const handleSelectUserSession = useCallback((us: UserSession) => {
    setUserSessionExpired(false);
    setSelectedUserSession(us);
    setView('userSessionDetail');
  }, []);

  // ── Refresh single user session ──
  const handleRefreshUserSession = useCallback(() => {
    if (!selectedSession || !selectedUserSession) return;
    const sessionId = getSessionId(selectedSession);
    setUserSessionRefreshing(true);
    StreamingEngineService.getUserSession(sessionId, selectedUserSession.UserSessionDocumentId)
      .then((data) => setSelectedUserSession(data))
      .catch((err) => {
        console.error('Failed to refresh user session', err);
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setUserSessionExpired(true);
          alertRef.current('This user session no longer exists or has expired.', 'warning');
        } else {
          alertRef.current('Failed to refresh user session.', 'error');
        }
      })
      .finally(() => setUserSessionRefreshing(false));
  }, [selectedSession, selectedUserSession]);

  // ── Reset session (force recovery under the same session id) ──
  const handleResetSession = useCallback(
    async (session: StreamingEngineSession) => {
      const sessionId = getSessionId(session);
      const confirmed = await vnConfirm({
        title: 'Reset Session',
        message: `Reset session ${sessionId}? Viewers keep the same playback URL. A new stream will start under this session.`,
        confirmText: 'Reset',
        type: 'danger',
      });
      if (!confirmed) return;

      setResettingSessionId(sessionId);
      try {
        await StreamingEngineService.resetSession(sessionId);
        alertRef.current(`Session ${sessionId} has been reset.`, 'success');
        await loadSessions();
        if (view === 'sessionDetail' && selectedSessionId === sessionId) {
          fetchSessionState(sessionId, false);
          fetchUserSessions(sessionId);
        }
      } catch (err) {
        console.error('Failed to reset session', err);
        const status = (err as { response?: { status?: number } })?.response?.status;
        const body = (err as { response?: { data?: unknown } })?.response?.data;
        const detail = typeof body === 'string' && body.trim() ? body : '';
        if (status === 404) {
          alertRef.current(`Session ${sessionId} was not found.`, 'error');
        } else {
          alertRef.current(detail || 'Failed to reset session.', 'error');
        }
      } finally {
        setResettingSessionId(null);
      }
    },
    [vnConfirm, loadSessions, view, selectedSessionId, fetchSessionState, fetchUserSessions]
  );

  // ── Toggle session live tracing ──
  const handleToggleLiveTracing = async () => {
    if (!selectedSessionId || !sessionState) return;
    setLiveTracingPatching(true);
    try {
      const newVal = !serverLiveTracingEnabled;
      await StreamingEngineService.setSessionLiveTracing(selectedSessionId, newVal);
      setSessionState((prev) => (prev ? { ...prev, enableLiveTracing: newVal } : null));
      if (newVal) {
        alertRef.current('Live Tracing has been enabled. It may take up to 1 minute to see events.', 'info');
      }
    } catch (err) {
      console.error('Failed to update live tracing', err);
      alertRef.current('Failed to update live tracing.', 'error');
    } finally {
      setLiveTracingPatching(false);
    }
  };

  // ── Guard: unsaved channel ──
  if (!channelId || clientUtils.isNullOrWhitespace(channelId)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Monitoring</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: mon.textMuted }}>
          Monitoring is available for existing channels only. Save the channel first to view sessions.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: '-12px', px: 0.5 }}>
      {view === 'list' && (
        <SessionListView
          sessions={sessions}
          loading={loading}
          onSelectSession={handleSelectSession}
          onRefresh={loadSessions}
          timezone={timezone}
        />
      )}

      {view === 'sessionDetail' && selectedSession && (
        <SessionDetailView
          session={selectedSession}
          sessionState={sessionState}
          detailLoading={detailLoading}
          userSessions={userSessions}
          userSessionsLoading={userSessionsLoading}
          liveTracingEnabled={serverLiveTracingEnabled}
          liveTracingPatching={liveTracingPatching}
          isSessionStopped={isSessionStopped}
          traceMessages={traceMessages}
          socketReady={readyState === ReadyState.OPEN}
          onBack={() => {
            setUserSessionExpired(false);
            setView('list');
          }}
          onToggleLiveTracing={handleToggleLiveTracing}
          onResetSession={() => handleResetSession(selectedSession)}
          resetting={resettingSessionId === getSessionId(selectedSession)}
          onSelectUserSession={handleSelectUserSession}
          onRefreshUserSessions={() => fetchUserSessions(getSessionId(selectedSession))}
          prevTraceCountRef={prevTraceCountRef}
          timezone={timezone}
        />
      )}

      {view === 'userSessionDetail' && selectedSession && selectedUserSession && (
        <UserSessionDetailView
          userSession={selectedUserSession}
          sessionId={getSessionId(selectedSession)}
          onBack={() => {
            setUserSessionExpired(false);
            setView('sessionDetail');
          }}
          onRefresh={handleRefreshUserSession}
          refreshing={userSessionRefreshing}
          timezone={timezone}
          userSessionExpired={userSessionExpired}
          onUserSessionUpdated={setSelectedUserSession}
        />
      )}
    </Box>
  );
};

export default ChannelMonitoring;
